import { Injectable } from '@angular/core';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { FundOverviewService } from '@products/services/fund-overview.service';
import {
  FundCommentaryDocsResponse,
  FundCommentaryDocument,
  ProductDetailConfiguration,
  FundComentaryContent,
} from '@types';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import documentFundQuery from '@graphql/fund-document/fund-documents.graphql';
import { Product } from '@models';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { IUserProfile } from '@services/profile.interface';
import { Logger } from '@utils/logger';
const logger = Logger.getLogger('FundCommentaryService');

@Injectable({ providedIn: 'root' })
export class FundCommentaryService {
  fundCommnetaryDocuments$: Observable<FundCommentaryDocsResponse>;
  dateFormat: string;

  constructor(
    private literatureDataService: LiteratureDataService,
    private overviewService: FundOverviewService,
    private literatureConfig: LiteratureGlobalConfigService
  ) {}

  getFundCommentaryContent(fundId: string): Observable<FundComentaryContent> {
    const paramMap = new Map();
    paramMap.set('fundid', fundId).set('start-date', this.currentDate());
    return this.literatureDataService.getFundCommentaryContent(paramMap, true);
  }

  private currentDate(): string {
    const date = new Date();
    return (
      date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear()
    );
  }
  /**
   * fetch the fund Commentary documents
   * @param productDetailConfiguration of type ProductDetailConfiguration
   * @returns of type Observable<FundCommentaryDocsResponse>
   */
  getCommentaryDocs(
    productDetailConfiguration: ProductDetailConfiguration
  ): Observable<FundCommentaryDocsResponse> {
    this.dateFormat = this.literatureConfig.dateFormat;
    const paramMap = new Map();
    paramMap
      .set('frkOneTISNumber', productDetailConfiguration.fundId)
      .set('shareClass', productDetailConfiguration.shareClassCode)
      .set('commentary', true);
    return this.literatureDataService.loadData(paramMap, true, true);
  }

  showCommentarySection$(
    params: ProductDetailConfiguration
  ): Observable<boolean> {
    const fundCommentaryContent$ = this.getFundCommentaryContent(
      params?.fundId
    ).pipe(
      catchError((err) => {
        if (err?.status === 404) {
          logger.warn(
            '[showCommentarySection] ' +
              err?.messaage +
              ' code: ' +
              err?.error?.error?.errorList[0]?.code
          );
        }
        return of({ documents: { count: 0 } });
      })
    );
    const fundCommentaryDocument$ = this.getCommentaryDocs({
      fundId: params?.fundId,
      shareClassCode: params?.shareClassCode,
    }).pipe(
      catchError((err) => {
        if (err?.status === 404) {
          logger.warn(
            '[getCommentaryDocs]' +
              err?.message +
              ' code: ' +
              err?.error?.message
          );
        }
        const regex: RegExp = /[?&]([^=#&]+)=([^&#]*)/g;
        const url: string = err?.url;
        const rquestParams: any = {};
        let match;
        match = regex.exec(url);
        while (match) {
          rquestParams[match[1]] = match[2];
          match = regex.exec(url);
        }
        if (rquestParams?.loggedIn === 'y') {
          return of({ document: [] });
        }
        return of({ document: ['please login'] });
      })
    );

    return combineLatest([
      fundCommentaryContent$,
      fundCommentaryDocument$,
    ]).pipe(
      map(
        (data) =>
          Number(data[0]?.documents?.count) > 0 || data[1]?.document?.length > 0
      )
    );
  }

  // ToDo: fix return type
  getDocumentDownloadUrl(doc: FundCommentaryDocument): string {
    return this.literatureDataService.getCommentaryDownloadDocUrl(doc);
  }

  getProfileData$(): Observable<IUserProfile> {
    return this.literatureDataService.getProfileData$();
  }
}
